import { Box, LinearProgress, Link, Stack, Typography } from "@mui/material";
import { EpisodicBadge } from "components/EpisodicBadge";
import { AccumulationLimitType, WasteContainerListItemFragment } from "generated-graphql/graphql";
import { Link as RouterLink } from "react-router-dom";

import { WasteClassificationChip } from "../WasteClassificationChip";
import {
  containerFillFraction,
  containerFillRatio,
  formatAmountCapacity,
  NO_VALUE_PLACEHOLDER,
  approachingNote,
  clockTextColor,
} from "./containerDisplay";
import { ClockLimitTooltip, WasteContainerClockChip } from "./WasteContainerClockChip";

type ContainerRow = WasteContainerListItemFragment;

/**
 * A container's label, marked when the container is accumulating under an
 * episodic event.
 *
 * The mark earns its place by changing what the row's other columns mean: the
 * clock beside it is the event's 60-day window rather than the facility's
 * standing limit, and the waste is exempt from the on-site cap. Without it a
 * reader scanning the grid has no way to tell why one drum's deadline doesn't
 * match its neighbours'.
 */
export function ContainerLabelCell({ row }: { row: ContainerRow }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap>
        {row.containerLabel}
      </Typography>
      {row.episodicEvent && <EpisodicBadge density="compact" />}
    </Stack>
  );
}

/**
 * The waste in a row: the stream's name, its vendor profile number, and how
 * it's classified.
 *
 * All three on one line because they answer one question — what is in this
 * drum — and a reader scanning for the acutely hazardous ones shouldn't have
 * to widen a second column to find out.
 */
export function WasteStreamCell({
  row,
  tenantId,
  linkStream,
}: {
  row: ContainerRow;
  tenantId: string;
  /**
   * The streams route is gated on manifest-views-v2, so a tenant with
   * container tracking but not that flag would click through to the waste
   * overview. Plain text for them.
   */
  linkStream: boolean;
}) {
  if (!row.wasteStream) {
    return NO_VALUE_PLACEHOLDER;
  }
  const profileNumber = row.wasteStream.profile?.profileNumber;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      {/* minWidth 0 so the name is what gives way when the cell is tight —
          without it a flex item refuses to shrink below its content and the
          chip beside it gets pushed out instead. */}
      {linkStream ? (
        <Link
          component={RouterLink}
          to={`/o/${tenantId}/waste/streams/${row.wasteStream.id}`}
          // Without this the row click fires too and the drawer opens on top
          // of the navigation.
          onClick={(event) => event.stopPropagation()}
          noWrap
          sx={{ minWidth: 0 }}
        >
          {row.wasteStream.name}
        </Link>
      ) : (
        <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
          {row.wasteStream.name}
        </Typography>
      )}
      {profileNumber && (
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {profileNumber}
        </Typography>
      )}
      <WasteClassificationChip
        classification={row.wasteStream.classification}
        variant="soft"
        sx={{ flexShrink: 0 }}
      />
    </Stack>
  );
}

/**
 * How full the container is: a bar over the numbers.
 *
 * The bar is omitted — not drawn empty — when the fraction can't be computed
 * (no capacity, or amounts in a unit the capacity isn't in), because an empty
 * bar reads as "this drum is empty" rather than "we can't say".
 */
export function FillCell({ row }: { row: ContainerRow }) {
  const fraction = containerFillFraction(row);
  // The clamped fraction sizes the bar; the raw ratio is the only thing that
  // can still tell an over-filled container from a full one.
  const ratio = containerFillRatio(row);
  return (
    <Stack spacing={0.5} justifyContent="center" sx={{ width: "100%" }}>
      {fraction !== null && (
        <LinearProgress
          variant="determinate"
          value={fraction * 100}
          // Over-full is a real state (an over-capacity container is a
          // compliance finding), so a full-looking bar is not enough — the
          // color is what distinguishes it.
          color={ratio !== null && ratio > 1 ? "error" : "primary"}
          sx={{ height: 4, borderRadius: 2 }}
        />
      )}
      <Typography variant="caption" color="text.secondary" noWrap>
        {formatAmountCapacity(row)}
      </Typography>
    </Stack>
  );
}

/** The soonest dated limit — the one actually driving the container's deadline. */
function governingTimeLimit(row: ContainerRow) {
  return row.clock.limits
    .filter(
      (limit) => limit.limitType === AccumulationLimitType.Time && limit.daysRemaining != null
    )
    .sort((a, b) => (a.daysRemaining ?? 0) - (b.daysRemaining ?? 0))[0];
}

/**
 * How long this container has left, in the words the specs use: "12 days", or
 * "3 days over" once the deadline has passed.
 *
 * Falls back to the status chip for containers whose limit isn't a countdown —
 * a satellite container bound by a volume threshold has no days to show, and
 * printing one would invent a deadline it doesn't have.
 */
export function ClockCell({
  row,
  showClockTooltip,
}: {
  row: ContainerRow;
  /** Name the rule behind the reading on hover. Off on the tenant-wide list,
   * on where the reader is looking at one area's containers. */
  showClockTooltip?: boolean;
}) {
  const limits = showClockTooltip ? row.clock.limits : undefined;
  const limit = governingTimeLimit(row);
  if (!limit || limit.daysRemaining == null) {
    // The status vocabulary carries the undetermined case itself
    // (STATUS_NEEDED), so the chip needs no extra branching here.
    return <WasteContainerClockChip status={row.clock.status} limits={limits} />;
  }
  const overdue = limit.daysRemaining < 0;
  const days = Math.abs(limit.daysRemaining);
  const color = clockTextColor(limit.status, overdue);
  return (
    <ClockLimitTooltip limits={limits} note={approachingNote(limit.status, overdue)}>
      <Box component="span" sx={{ color }}>
        <Typography variant="body2" component="span" color="inherit">
          {`${days} ${days === 1 ? "day" : "days"}${overdue ? " over" : ""}`}
        </Typography>
      </Box>
    </ClockLimitTooltip>
  );
}
